'use client';

import { useEffect, useState } from 'react';

export const DEFERRED_QUERY_DELAY_MS = 2000;

/**
 * Latches to `true` after a short delay so secondary react-query fetches can
 * be ANDed out of a page's startup window. The PR page fires 15-20 requests
 * on mount; navigations during that window (e.g. bouncing back to the inbox)
 * queue behind them. Deferring below-the-fold data keeps the first seconds
 * free for content and navigation.
 *
 * Cached query data still renders immediately while `enabled` is false —
 * react-query only skips the (re)fetch, so revisits are unaffected.
 */
export function useDeferredQueryEnabled(delayMs: number = DEFERRED_QUERY_DELAY_MS): boolean {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (isReady) {
      return;
    }

    const timeoutId = setTimeout(() => setIsReady(true), delayMs);
    return () => clearTimeout(timeoutId);
  }, [delayMs, isReady]);

  return isReady;
}
